import React from 'react';
import { motion } from 'framer-motion';
import { ProgressIndicator } from './ProgressIndicator';
interface SlideHookProps {
  onNext: () => void;
}
function WaveformVisual() {
  const paths = ['M0,50 Q50,20 100,50 T200,50 T300,50 T400,50 T500,50 T600,50 T700,50 T800,50', 'M0,50 Q50,35 100,50 T200,50 T300,50 T400,50 T500,50 T600,50 T700,50 T800,50', 'M0,50 Q50,10 100,50 T200,50 T300,50 T400,50 T500,50 T600,50 T700,50 T800,50', 'M0,50 Q50,42 100,50 T200,50 T300,50 T400,50 T500,50 T600,50 T700,50 T800,50'];
  const sharpPaths = ['M0,50 C80,15 120,85 200,50 C280,15 320,85 400,50 C480,15 520,85 600,50 C680,15 720,85 800,50', 'M0,50 C80,30 120,70 200,50 C280,30 320,70 400,50 C480,30 520,70 600,50 C680,30 720,70 800,50', 'M0,50 C80,5 120,95 200,50 C280,5 320,95 400,50 C480,5 520,95 600,50 C680,5 720,95 800,50', 'M0,50 C80,38 120,62 200,50 C280,38 320,62 400,50 C480,38 520,62 600,50 C680,38 720,62 800,50'];
  const opacities = [0.4, 0.2, 0.5, 0.15];
  const strokeWidths = [1.5, 1, 2, 0.8];
  const delays = [0, 0.3, 0.6, 1.0];
  return <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none" data-id="element-108">
      <svg viewBox="0 0 800 100" className="w-full max-w-4xl h-auto opacity-40" preserveAspectRatio="xMidYMid meet" data-id="element-109">
        {sharpPaths.map((d, i) => <motion.path key={i} d={d} fill="none" stroke="url(#waveGradient)" strokeWidth={strokeWidths[i]} strokeLinecap="round" initial={{
        pathLength: 0,
        opacity: 0,
        filter: 'blur(4px)'
      }} animate={{
        pathLength: 1,
        opacity: opacities[i],
        filter: 'blur(0px)'
      }} transition={{
        pathLength: {
          duration: 2.5,
          delay: delays[i],
          ease: 'easeInOut'
        },
        opacity: {
          duration: 2,
          delay: delays[i],
          ease: 'easeOut'
        },
        filter: {
          duration: 3,
          delay: delays[i] + 0.5,
          ease: 'easeOut'
        }
      }} data-id="element-110" />)}
        <defs data-id="element-111">
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-112">
            <stop offset="0%" stopColor="#00B4D8" stopOpacity="0.1" data-id="element-113" />
            <stop offset="30%" stopColor="#00B4D8" stopOpacity="0.6" data-id="element-114" />
            <stop offset="50%" stopColor="#0891B2" stopOpacity="0.8" data-id="element-115" />
            <stop offset="70%" stopColor="#00B4D8" stopOpacity="0.6" data-id="element-116" />
            <stop offset="100%" stopColor="#00B4D8" stopOpacity="0.1" data-id="element-117" />
          </linearGradient>
        </defs>
      </svg>
    </div>;
}
export function SlideHook({
  onNext
}: SlideHookProps) {
  return <div className="relative w-full h-full flex flex-col items-center justify-center px-6 overflow-hidden" data-id="element-118">
      <WaveformVisual data-id="element-119" />

      <div className="relative z-10 max-w-3xl mx-auto text-center flex flex-col items-center" data-id="element-120">
        <motion.h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight tracking-tight text-slate-900 mb-6" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} data-id="element-121">
          When new options emerge,{' '}
          <span className="text-teal-600" data-id="element-122">what should define your choice?</span>
        </motion.h1>

        <motion.p className="text-lg sm:text-xl text-slate-500 max-w-2xl mb-10 leading-relaxed" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.7
      }} data-id="element-123">
          In HAE prophylaxis, treatment decisions are evolving — but not all
          evidence is equal.
        </motion.p>

        <motion.button onClick={onNext} className="px-8 py-3.5 bg-takeda-red text-white font-semibold rounded-lg text-base
                     hover:bg-takeda-darkred transition-all duration-300
                     shadow-lg shadow-red-500/20 
                     focus:outline-none focus:ring-2 focus:ring-takeda-red focus:ring-offset-2 focus:ring-offset-white" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 1.1
      }} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.98
      }} aria-label="Take a quick view — proceed to next slide" data-id="element-124">
          Take a quick view
        </motion.button>

        <motion.div className="mt-12" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 0.6
      }} data-id="element-125">
          <ProgressIndicator currentStep={1} totalSteps={7} data-id="element-126" />
        </motion.div>
      </div>
    </div>;
}